/**
 * ⚠️ CRITICAL: CDV Model Display Name Standard
 *
 * When displaying model names ANYWHERE on the site, ALWAYS prefer database displayName:
 *
 * PRIMARY METHOD (Preferred):
 *   - CDV pages: response.model.displayName
 *   - Scores Matrix: score.modelDisplayName
 *   - Standard names: GPT-4, DeepSeek, Llama, Claude, Grok, Gemini
 *
 * FALLBACK METHOD (Only when displayName unavailable):
 *   - Use: formatModelDisplayName(modelId)
 *   - This function should be FALLBACK only, not primary
 *
 * WHY: Database displayName provides consistent branding across all pages.
 *
 * Example (CORRECT):
 *   {score.modelDisplayName || formatModelDisplayName(score.modelId)}
 *
 * Example (WRONG):
 *   {formatModelDisplayName(score.modelId)}  // Should check displayName first
 *   {score.modelName}  // Shows technical name like "gpt-4o-mini"
 *
 * See:
 *   - docs/MODEL_DISPLAY_STANDARDS.md - Full documentation
 *   - .claude/protocols/model-display-protocol.md - Claude protocol
 */

export interface ModelMetadataEntry {
  displayName?: string;
  providerName?: string;
}

export type ModelMetadataMap = Map<string, ModelMetadataEntry>;

const SPECIAL_MODEL_NAMES: Record<string, string> = {
  // Normalized model IDs (used in comparisons.models_used)
  'gpt-4': 'GPT-4',
  'claude-3': 'Claude',
  'gemini-pro': 'Gemini',
  'deepseek': 'DeepSeek',
  'gemini': 'Gemini',
  'llama': 'Llama',
  'grok': 'Grok',

  // Judge model IDs (used in enhanced_response_scores table)
  // These should map to standardized short names for consistency with comparison list display
  // NOTE: Include both original case and lowercase versions for case-insensitive lookup
  'Llama-3.3-70B-Instruct': 'Llama',
  'llama-3.3-70b-instruct': 'Llama',
  'gemini-2.0-flash': 'Gemini',
  'gpt-4o-mini': 'GPT-4',
  'grok-2-1212': 'Grok',
  'grok-3-mini': 'Grok',
  'deepseek-chat': 'DeepSeek',
  'claude': 'Claude',

  // Technical model names (used in llm_models.name)
  'gpt-4o': 'GPT-4',
  'gpt-4.1': 'GPT-4',
  'gpt-4-turbo': 'GPT-4',
  'gpt-4.1-mini': 'GPT-4',
  'o1-mini': 'GPT-4',
  'o1-preview': 'GPT-4',
  'claude-3-5-sonnet-20241022': 'Claude',
  'claude-sonnet-3-5': 'Claude',
  'claude-3-5-haiku-20241022': 'Claude',
  'claude-3-opus-20240229': 'Claude',
  'claude-3-haiku-20240307': 'Claude',
  'claude-3-sonnet-20240229': 'Claude',
  'gemini-2.0-flash-exp': 'Gemini',
  'gemini-2.5-flash': 'Gemini',
  'gemini-1.5-flash': 'Gemini',
  'gemini-1.5-pro': 'Gemini',
  'meta-llama-3.1-405b-instruct': 'Llama',
  'meta-llama-3.1-70b-instruct': 'Llama',
  'meta-llama-3.1-8b-instruct': 'Llama',
  'mistral-large-2411': 'Mistral',
  'mistral-small-2411': 'Mistral',
  'mistral-nemo': 'Mistral',
  'deepseek-reasoner': 'DeepSeek',
  'grok-2': 'Grok',
  'grok-beta': 'Grok',
};

const PROVIDER_PREFIX_TOKENS = new Set([
  'openai',
  'anthropic',
  'google',
  'azure',
  'aws',
  'amazon',
  'bedrock',
]);

export const normalizeModelIdentifier = (modelId: string): string => {
  if (!modelId) {
    return modelId;
  }

  const trimmed = modelId.trim();
  if (!trimmed) {
    return trimmed;
  }

  let normalized = trimmed.toLowerCase();

  if (normalized.startsWith('model-')) {
    normalized = normalized.slice('model-'.length);
  } else if (normalized.startsWith('model_')) {
    normalized = normalized.slice('model_'.length);
  }

  const segments = normalized.split(/[-_]/).filter(Boolean);
  if (segments.length === 0) {
    return normalized;
  }

  while (segments.length > 1 && PROVIDER_PREFIX_TOKENS.has(segments[0])) {
    segments.shift();
  }

  return segments.join('-') || normalized;
};

export const formatModelDisplayName = (
  modelId: string,
  detailsMap?: ModelMetadataMap
): string => {
  const directDetail = detailsMap?.get(modelId);
  if (directDetail?.displayName) {
    return directDetail.displayName;
  }

  const normalizedId = normalizeModelIdentifier(modelId) || modelId.toLowerCase();
  if (SPECIAL_MODEL_NAMES[normalizedId]) {
    return SPECIAL_MODEL_NAMES[normalizedId];
  }

  return normalizedId
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .map((segment) => {
      if (!segment) {
        return segment;
      }

      if (segment === segment.toUpperCase()) {
        return segment;
      }

      if (segment.length <= 3) {
        return segment.toUpperCase();
      }

      return segment.charAt(0).toUpperCase() + segment.slice(1);
    })
    .join(' ');
};

export const inferProviderName = (
  modelId: string,
  detailsMap?: ModelMetadataMap
): string => {
  const detail = detailsMap?.get(modelId);
  if (detail?.providerName) {
    return detail.providerName;
  }

  const normalized = modelId.toLowerCase();
  if (normalized.includes('claude')) return 'Anthropic';
  if (normalized.includes('gpt') || normalized.startsWith('o1')) return 'OpenAI';
  if (normalized.includes('gemini')) return 'Google';
  if (normalized.includes('grok')) return 'xAI';
  if (normalized.includes('llama') || normalized.includes('meta')) return 'Meta';
  if (normalized.includes('mistral')) return 'Mistral';
  if (normalized.includes('deepseek')) return 'DeepSeek';
  if (normalized.includes('cohere')) return 'Cohere';
  if (normalized.includes('command')) return 'Cohere';
  if (normalized.includes('sonnet') || normalized.includes('opus') || normalized.includes('haiku')) {
    return 'Anthropic';
  }
  return 'Unknown Provider';
};

export const getModelDisplayInfo = (
  modelId: string,
  detailsMap?: ModelMetadataMap
): {
  displayName: string;
  providerName: string;
} => ({
  displayName: formatModelDisplayName(modelId, detailsMap),
  providerName: inferProviderName(modelId, detailsMap),
});

/**
 * Build a standardized modelKey from a response object
 *
 * This ensures consistent modelKey construction across the app, matching the format
 * used by the consensus backend: "provider:model"
 *
 * Example: "openai:gpt-4o-mini", "anthropic:claude-3-5-sonnet-20241022"
 *
 * @param response - Response object with provider and model information
 * @returns Standardized modelKey string
 */
export const buildModelKey = (response: {
  provider?: { name?: string } | null;
  providerName?: string;
  providerId?: string;
  model?: { name?: string } | null;
  modelName?: string;
  modelId?: string;
}): string => {
  const provider = response.provider?.name || response.providerName || response.providerId || 'unknown';
  const model = response.model?.name || response.modelName || response.modelId || 'unknown';
  return `${provider}:${model}`;
};
